import { FunctionComponent, useMemo } from 'react';
import { ColDef, GridOptions, RowDoubleClickedEvent } from 'ag-grid-community';
import { BASE_GRID_OPTIONS, PoGrid } from '@/components/grid/PoGrid';
import { ColumnID } from '@/components/grid/column-ids';
import { useTranslation } from '@/lib';
import { BalancesAtLocationModel } from '@/modules/balances/models/BalanceAtLocationModel';

interface GuestLocationsGridProps {
  data: BalancesAtLocationModel[] | undefined;
  isLoading: boolean;
  onLocationSelected: (location: BalancesAtLocationModel) => void;
}

const GuestLocationsGrid: FunctionComponent<GuestLocationsGridProps> = ({ data, isLoading, onLocationSelected }) => {
  const { t } = useTranslation();

  const columnDefs: ColDef<BalancesAtLocationModel>[] = useMemo(
    () => [
      {
        headerName: t('name'),
        field: 'dto.locationName',
        colId: ColumnID.LOCATION_NAME,
        sortable: true,
        filter: true,
        resizable: true,
        flex: 1,
      },
      {
        headerName: t('code'),
        field: 'dto.locationCode',
        colId: ColumnID.LOCATION_CODE,
        sortable: true,
        filter: true,
        resizable: true,
        flex: 1,
      },
      {
        headerName: t('total_balance'),
        field: 'dto.totalBalance',
        sortable: true,
        filter: true,
        resizable: true,
        type: 'numericColumn',
        flex: 1,
      },
      {
        headerName: `${t('incoming')} (30d)`,
        field: 'dto.totalIncoming',
        sortable: true,
        filter: true,
        resizable: true,
        type: 'numericColumn',
        flex: 1,
      },
      {
        headerName: `${t('outgoing')} (30d)`,
        field: 'dto.totalOutgoing',
        sortable: true,
        filter: true,
        resizable: true,
        type: 'numericColumn',
        flex: 1,
      },
      {
        headerName: `30d+ ${t('staytime')}`,
        field: 'dto.totalStaleAssets',
        sortable: true,
        filter: true,
        resizable: true,
        type: 'numericColumn',
        flex: 1,
      },
    ],
    [t],
  );

  const gridOptions: GridOptions<BalancesAtLocationModel> = useMemo(() => {
    const handleRowDoubleClicked = (event: RowDoubleClickedEvent<BalancesAtLocationModel>) => {
      if (event.data) {
        onLocationSelected(event.data);
      }
    };

    return {
      ...BASE_GRID_OPTIONS,
      onRowDoubleClicked: handleRowDoubleClicked,
      getRowId: (params) => params.data.dto.locationId.toString(),
      getContextMenuItems: () => ['copy'],
    };
  }, [onLocationSelected, t]);

  return <PoGrid rowData={data} colDefs={columnDefs} gridOptions={gridOptions} isLoading={isLoading} />;
};

export default GuestLocationsGrid;
